import { formatLongIsoDate, isPastIsoDate } from '@upms/dates';
import { orderBy } from 'lodash';
// Type-only: keeps the curated-status union in one place without pulling the
// policy SDK or react-query into this module.
import type { getCuratedStatus } from '@/hooks/usePolicy';
import type { PaymentPlan } from '@/lib/api/billing/types';
import { isPayrollPaymentPlan } from '@/lib/api/billing/predicates';

export type PolicySummary = {
  curatedStatus: ReturnType<typeof getCuratedStatus>;
  effectiveDate: string;
  plan: PaymentPlan | undefined;
  policyId: string;
};

const USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

type ScheduledPayment = { amount: number; dueDate: string };

/**
 * Every policy's scheduled payment, in the order the policies were given. Past
 * due dates are kept — arrears are the most important thing to say — but
 * Payroll Billing policies are skipped: their premium is collected by the
 * payroll partner, and their cards never show an amount, so naming one here
 * would contradict the card right below the header.
 */
const getScheduledPayments = (policies: PolicySummary[]): ScheduledPayment[] => {
  return policies.flatMap(({ plan }) =>
    plan &&
    !isPayrollPaymentPlan(plan) &&
    plan.nextPaymentAmount != null &&
    plan.nextPaymentAmount > 0 &&
    plan.nextPaymentDueDate
      ? [{ amount: plan.nextPaymentAmount, dueDate: plan.nextPaymentDueDate }]
      : []
  );
};

/**
 * The state of the policyholder's coverage.
 *
 * Depends only on how many policies are active and pending, so it can never
 * contradict the money sentence or the policy cards. Expired and cancelled
 * policies are never counted and never trigger their own state.
 */
export const getCoverageSentence = (policies: PolicySummary[]): string => {
  const activeCount = policies.filter((policy) => policy.curatedStatus === 'active').length;
  const pending = policies.filter((policy) => policy.curatedStatus === 'pending');

  if (activeCount === 0) {
    if (pending.length === 0) {
      return 'You don’t currently have active coverage with Pie.';
    }

    // The soonest date coverage begins — not the card order, which is newest first.
    const [startsOn] = pending.map((policy) => policy.effectiveDate).sort();

    return `Your coverage takes effect on ${formatLongIsoDate(startsOn)}.`;
  }

  const policyWord = activeCount === 1 ? 'policy' : 'policies';

  if (pending.length === 0) {
    return `You have ${activeCount} active ${policyWord}.`;
  }

  return `You have ${activeCount} active ${policyWord} and ${pending.length} pending.`;
};

/**
 * The one money fact that matters, or nothing at all.
 *
 * The oldest unfulfilled transaction wins, so a payment already in arrears
 * outranks everything — it is the thing the policyholder most needs to act on.
 * A credit has no date of its own to age (billing exposes no credit field, only
 * a negative remaining premium), so it cannot be ordered against a payment by
 * date; it sits between arrears and an upcoming payment:
 *
 *   1. the oldest past due payment
 *   2. a credit
 *   3. the soonest upcoming payment
 *   4. payroll billing, when every active policy is on it
 *
 * Every policy is considered at each step — a credit on a cancelled policy, or
 * a balance on an expired one, is still the policyholder's money.
 */
export const getMoneySentence = (policies: PolicySummary[]): string | null => {
  // Newest term first, mirroring the `PolicyList` sort in `YourPolicies`
  // (`initialSortKey: 'effectiveDate'`, `initialSortDirection: DESCENDING`), so
  // the header and the policy cards agree on which policy comes first.
  const inCardOrder = orderBy(policies, 'effectiveDate', 'desc');

  // Oldest first. `orderBy` is a stable sort, so ordering a card-ordered list
  // keeps the newest policy term as the tiebreak when two payments share a day.
  const [earliest] = orderBy(getScheduledPayments(inCardOrder), 'dueDate');

  if (earliest && isPastIsoDate(earliest.dueDate)) {
    const amount = USD.format(earliest.amount);
    const date = formatLongIsoDate(earliest.dueDate);

    return `Your payment of ${amount} was due on ${date} and is currently past due.`;
  }

  // A negative remaining premium is the only signal that the policyholder has
  // overpaid. Undated, so the newest policy term picks which credit to name.
  const credit = inCardOrder.find((policy) => (policy.plan?.totalRemainingPremium ?? 0) < 0)?.plan;

  if (credit) {
    const amount = USD.format(Math.abs(credit.totalRemainingPremium));

    return `You have a credit of ${amount}, which will be applied to your account.`;
  }

  if (earliest) {
    const amount = USD.format(earliest.amount);

    return `Your next payment of ${amount} is scheduled for ${formatLongIsoDate(earliest.dueDate)}.`;
  }

  const active = policies.filter((policy) => policy.curatedStatus === 'active');

  if (
    active.length > 0 &&
    active.every((policy) => policy.plan && isPayrollPaymentPlan(policy.plan))
  ) {
    return 'Your premium payments are managed through your payroll partner.';
  }

  return null;
};

/**
 * The home page header's one-line summary: what the policyholder's coverage is
 * doing, then the one money thing they need to know. An account with no
 * policies falls through to the no-coverage sentence.
 */
export const buildDescription = (policies: PolicySummary[]): string => {
  const coverage = getCoverageSentence(policies);
  const money = getMoneySentence(policies);

  return money === null ? coverage : `${coverage} ${money}`;
};
